import { useEffect, useMemo, useState } from 'react';
import { Customer, Loan } from '@/hooks/useSupabaseStore';
import { fetchCustomerImages, type CustomerImages } from '@/lib/customerImages';

interface Props {
  customer: Customer;
  loans: Loan[];
  onClose: () => void;
}

const statusConfigMap = {
  active: { label: 'Đang vay', cls: 'bg-blue-100 text-blue-700' },
  overdue: { label: 'Quá hạn', cls: 'bg-red-100 text-red-700' },
  due_soon: { label: 'Sắp đến hạn', cls: 'bg-amber-100 text-amber-700' },
  redeemed: { label: 'Đã chuộc', cls: 'bg-emerald-100 text-emerald-700' },
  forfeited: { label: 'Đã thanh lý', cls: 'bg-gray-100 text-gray-600' },
  interest_paid: { label: 'Đã đóng lãi', cls: 'bg-purple-100 text-purple-700' },
};

export default function CustomerViewModal({ customer, loans, onClose }: Props) {
  const customerLoans = useMemo(() => {
    return loans
      .filter((l) => l.customerId === customer.id)
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [loans, customer.id]);

  const activeLoans = customerLoans.filter((l) => l.status !== 'redeemed' && l.status !== 'forfeited');
  const pastLoans = customerLoans.filter((l) => l.status === 'redeemed' || l.status === 'forfeited');
  const totalBorrowed = activeLoans.reduce((sum, l) => sum + l.loanAmount, 0);

  const [cccd, setCccd] = useState<CustomerImages>({});
  useEffect(() => {
    let cancelled = false;
    fetchCustomerImages(customer.id).then(imgs => {
      if (!cancelled) setCccd(imgs);
    });
    return () => { cancelled = true; };
  }, [customer.id]);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <span className="text-amber-700 font-bold text-base">{customer.name.charAt(0)}</span>
            </div>
            <div>
              <h3 className="font-bold text-gray-800">{customer.name}</h3>
              <p className="text-xs text-gray-400">{customer.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Thông tin cá nhân */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Thông tin cá nhân</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Họ tên', value: customer.name },
                { label: 'Số điện thoại', value: customer.phone },
                { label: 'CMND/CCCD', value: customer.idCard },
                { label: 'Ngày đăng ký', value: customer.createdAt },
                { label: 'Địa chỉ', value: customer.address || '—', span: 2 },
              ].map((item) => (
                <div key={item.label} className={item.span === 2 ? 'col-span-2' : ''}>
                  <p className="text-gray-400 text-xs mb-0.5">{item.label}</p>
                  <p className="text-gray-800 text-sm font-medium">{item.value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 bg-amber-50 rounded-xl p-4">
            <div className="text-center">
              <p className="text-amber-600 font-bold text-xl">{customerLoans.length}</p>
              <p className="text-gray-500 text-xs mt-0.5">Tổng lần vay</p>
            </div>
            <div className="text-center border-x border-amber-200">
              <p className="text-blue-600 font-bold text-xl">{activeLoans.length}</p>
              <p className="text-gray-500 text-xs mt-0.5">Đang vay</p>
            </div>
            <div className="text-center">
              <p className="text-gray-800 font-bold text-base">{(totalBorrowed / 1000000).toFixed(1)}M</p>
              <p className="text-gray-500 text-xs mt-0.5">Tổng dư nợ</p>
            </div>
          </div>

          {/* CCCD */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-id-card-line text-gray-400"></i>
              </span>
              Hình ảnh CCCD
            </p>
            {cccd.front || cccd.back ? (
              <div className="grid grid-cols-2 gap-3">
                {cccd.front && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1.5 font-medium">Mặt trước</p>
                    <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: 120 }}>
                      <img src={cccd.front} alt="CCCD mặt trước" className="w-full h-full object-cover" />
                    </div>
                    <a
                      href={cccd.front}
                      download={`CCCD_${customer.name}_mat_truoc.jpg`}
                      className="mt-1.5 flex items-center justify-center gap-1 w-full py-1.5 rounded-lg bg-gray-50 hover:bg-amber-50 text-gray-500 hover:text-amber-600 text-xs font-medium transition-colors cursor-pointer border border-gray-100 hover:border-amber-200"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-download-line text-sm"></i>
                      </span>
                      Tải về
                    </a>
                  </div>
                )}
                {cccd.back && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1.5 font-medium">Mặt sau</p>
                    <div className="rounded-xl overflow-hidden border border-gray-100" style={{ height: 120 }}>
                      <img src={cccd.back} alt="CCCD mặt sau" className="w-full h-full object-cover" />
                    </div>
                    <a
                      href={cccd.back}
                      download={`CCCD_${customer.name}_mat_sau.jpg`}
                      className="mt-1.5 flex items-center justify-center gap-1 w-full py-1.5 rounded-lg bg-gray-50 hover:bg-amber-50 text-gray-500 hover:text-amber-600 text-xs font-medium transition-colors cursor-pointer border border-gray-100 hover:border-amber-200"
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-download-line text-sm"></i>
                      </span>
                      Tải về
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-200 rounded-xl py-6 text-center text-gray-400">
                <span className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                  <i className="ri-id-card-line text-2xl"></i>
                </span>
                <p className="text-xs">Chưa có ảnh CCCD</p>
              </div>
            )}
          </div>

          {/* Lịch sử khoản vay */}
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-file-list-3-line text-gray-400"></i>
              </span>
              Lịch sử khoản vay
            </p>
            <div className="space-y-3">
              {activeLoans.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-blue-600 mb-2">Đang vay ({activeLoans.length})</p>
                  <div className="space-y-2">
                    {activeLoans.map((loan) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const due = new Date(loan.dueDate);
                      due.setHours(0, 0, 0, 0);
                      const daysLeft = Math.ceil((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
                      const isOverdue = daysLeft < 0;
                      const isDueSoon = daysLeft >= 0 && daysLeft <= 7;
                      const cfg = statusConfigMap[loan.status];
                      return (
                        <div key={loan.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50/50">
                          <div className="flex items-start justify-between gap-2 mb-1.5">
                            <div>
                              <p className="text-sm font-bold text-gray-800">{loan.id}</p>
                              <p className="text-xs text-gray-500">
                                {loan.assetName} · {loan.assetCategory}
                              </p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.cls}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                            <p className="text-xs text-gray-500">
                              Vay:{" "}
                              <span className="font-semibold text-gray-700">
                                {loan.loanAmount.toLocaleString('vi-VN')}đ
                              </span>
                            </p>
                            <p className="text-xs text-gray-500">
                              Lãi:{" "}
                              <span className="font-semibold text-gray-700">{loan.interestRate}%/tháng</span>
                            </p>
                            <p className="text-xs text-gray-500">
                              Bắt đầu:{" "}
                              <span className="text-gray-700">{loan.startDate}</span>
                            </p>
                            <p className="text-xs text-gray-500">
                              Hạn trả:{" "}
                              <span
                                className={`font-semibold ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-600' : 'text-gray-700'}`}
                              >
                                {loan.dueDate}
                                {isOverdue ? ` (quá ${Math.abs(daysLeft)} ngày)` : isDueSoon ? ` (${daysLeft} ngày)` : ''}
                              </span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {pastLoans.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Đã đóng ({pastLoans.length})</p>
                  <div className="space-y-2">
                    {pastLoans.map((loan) => {
                      const cfg = statusConfigMap[loan.status];
                      return (
                        <div key={loan.id} className="border border-gray-100 rounded-xl p-3 bg-gray-50/30 opacity-80">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div>
                              <p className="text-sm font-bold text-gray-700">{loan.id}</p>
                              <p className="text-xs text-gray-500">{loan.assetName}</p>
                            </div>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${cfg.cls}`}>
                              {cfg.label}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                            <p className="text-xs text-gray-500">
                              Vay:{" "}
                              <span className="font-semibold text-gray-600">
                                {loan.loanAmount.toLocaleString('vi-VN')}đ
                              </span>
                            </p>
                            <p className="text-xs text-gray-500">
                              Ngày đóng:{" "}
                              <span className="text-gray-600">{loan.dueDate}</span>
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {customerLoans.length === 0 && (
                <div className="border-2 border-dashed border-gray-200 rounded-xl py-6 text-center text-gray-400">
                  <span className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </span>
                  <p className="text-xs">Chưa có khoản vay nào</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}